import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

type InfoTooltipIconProps = {
  tooltip: string;
  ariaLabel?: string;
  className?: string;
  tooltipClassName?: string;
};

const InfoTooltipIcon: React.FC<InfoTooltipIconProps> = ({
  tooltip,
  ariaLabel,
  className = '',
  tooltipClassName = '',
}) => {
  const [hoverOpen, setHoverOpen] = React.useState(false);
  const [pinnedOpen, setPinnedOpen] = React.useState(false);

  const handleTooltipOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (pinnedOpen && !nextOpen) {
        return;
      }
      setHoverOpen(nextOpen);
    },
    [pinnedOpen]
  );

  const togglePinnedTooltip = React.useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPinnedOpen((previous) => {
      const next = !previous;
      if (!next) {
        setHoverOpen(false);
      }
      return next;
    });
  }, []);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setPinnedOpen((previous) => {
        const next = !previous;
        if (!next) {
          setHoverOpen(false);
        }
        return next;
      });
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setPinnedOpen(false);
      setHoverOpen(false);
    }
  }, []);

  const handleDismissPinnedTooltip = React.useCallback(() => {
    setPinnedOpen(false);
    setHoverOpen(false);
  }, []);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={pinnedOpen || hoverOpen} onOpenChange={handleTooltipOpenChange}>
        <TooltipTrigger asChild>
          <span
            aria-label={ariaLabel || tooltip}
            role="button"
            tabIndex={0}
            onClick={togglePinnedTooltip}
            onKeyDown={handleKeyDown}
            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[var(--qcl-border)] bg-white text-[10px] font-semibold leading-none text-[var(--qcl-text-muted)] cursor-help select-none ${className}`}
          >
            i
          </span>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={6}
          className={`max-w-[240px] whitespace-pre-line text-left text-xs leading-relaxed ${tooltipClassName}`}
          onEscapeKeyDown={handleDismissPinnedTooltip}
          onPointerDownOutside={handleDismissPinnedTooltip}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default InfoTooltipIcon;
